import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconSearch,
  IconMessage2,
  IconAlertCircle,
  IconRefresh,
  IconBuildingStore,
} from '@tabler/icons-react';
import styles from './AdminFeedbackSection.module.scss';
import { useGetAdminFeedback, useGetAdminFeedbackStats } from '../../api/admin-feedback-api/admin-feedback-api';
import type { FeedbackTicketDto, FeedbackStatsDto } from '../../api/model';
import { FeedbackStatus } from '../../api/model';
import {
  STATUS_COLOR,
  PRIORITY_COLOR,
  PRIORITY_WEIGHT,
  STATUS_VALUES,
  TYPE_VALUES,
  PRIORITY_VALUES,
  TYPE_ICON,
} from '../Feedbacks/feedbackMeta';
import { formatRelative, ticketRef } from '../Feedbacks/feedbackFormat';
import AdminFeedbackDetail from './AdminFeedbackDetail';

type SortKey = 'recent' | 'created' | 'priority';

const tint = (hex: string) => `${hex}1a`;

const activityTime = (tk: FeedbackTicketDto): number => {
  const v = tk.lastActivityAt || tk.modified || tk.created;
  return v ? new Date(v).getTime() : 0;
};

const AdminFeedbackSection: React.FC = () => {
  const { t, i18n } = useTranslation('feedback');
  const locale = i18n.language;

  const { data, isLoading, isError, refetch } = useGetAdminFeedback();
  const { data: stats } = useGetAdminFeedbackStats();
  const tickets = useMemo<FeedbackTicketDto[]>(() => data ?? [], [data]);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortKey>('recent');

  const filtered = useMemo<FeedbackTicketDto[]>(() => {
    const term = searchTerm.trim().toLowerCase();
    let result = tickets.filter((tk) => {
      if (term) {
        const haystack = [tk.title, tk.description, tk.reporterName, tk.tenantName, ticketRef(tk.id)]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (statusFilter && tk.status !== statusFilter) return false;
      if (typeFilter && tk.type !== typeFilter) return false;
      if (priorityFilter && tk.priority !== priorityFilter) return false;
      return true;
    });

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'created':
          return (b.created ? new Date(b.created).getTime() : 0) - (a.created ? new Date(a.created).getTime() : 0);
        case 'priority': {
          const pa = a.priority ? PRIORITY_WEIGHT[a.priority] : 0;
          const pb = b.priority ? PRIORITY_WEIGHT[b.priority] : 0;
          if (pb !== pa) return pb - pa;
          return activityTime(b) - activityTime(a);
        }
        case 'recent':
        default:
          return activityTime(b) - activityTime(a);
      }
    });

    return result;
  }, [tickets, searchTerm, statusFilter, typeFilter, priorityFilter, sortBy]);

  const selected = useMemo(
    () => (selectedId !== null ? tickets.find((tk) => tk.id === selectedId) ?? null : null),
    [tickets, selectedId],
  );

  const hasFilters = !!statusFilter || !!typeFilter || !!priorityFilter || !!searchTerm.trim();
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setTypeFilter('');
    setPriorityFilter('');
  };

  const statTiles: { key: string; label: string; value: number }[] = useMemo(() => {
    const s: FeedbackStatsDto = stats ?? {};
    return [
      { key: '', label: t('adminConsole.stats.total'), value: s.total ?? 0 },
      { key: FeedbackStatus.OPEN, label: t('statuses.OPEN'), value: s.open ?? 0 },
      { key: FeedbackStatus.IN_PROGRESS, label: t('statuses.IN_PROGRESS'), value: s.inProgress ?? 0 },
      { key: FeedbackStatus.NEEDS_INFO, label: t('statuses.NEEDS_INFO'), value: s.needsInfo ?? 0 },
      { key: FeedbackStatus.RESOLVED, label: t('statuses.RESOLVED'), value: s.resolved ?? 0 },
      { key: FeedbackStatus.CLOSED, label: t('statuses.CLOSED'), value: s.closed ?? 0 },
    ];
  }, [stats, t]);

  const renderList = () => {
    if (isLoading) {
      return <div className={styles.stateBox}>{t('list.loading')}</div>;
    }
    if (isError) {
      return (
        <div className={styles.stateBox}>
          <IconAlertCircle size={26} className={styles.stateIcon} />
          <p>{t('adminConsole.loadError')}</p>
          <button className={styles.linkButton} onClick={() => refetch()} type="button">
            <IconRefresh size={14} />
            {t('actions.retry')}
          </button>
        </div>
      );
    }
    if (filtered.length === 0) {
      return (
        <div className={styles.stateBox}>
          <IconMessage2 size={28} className={styles.stateIcon} />
          <p>{tickets.length === 0 ? t('adminConsole.empty') : t('adminConsole.noMatch')}</p>
          {hasFilters && (
            <button className={styles.linkButton} onClick={clearFilters} type="button">
              {t('filters.clear')}
            </button>
          )}
        </div>
      );
    }

    return filtered.map((tk) => {
      const statusColor = tk.status ? STATUS_COLOR[tk.status] : '#6b7280';
      const priorityColor = tk.priority ? PRIORITY_COLOR[tk.priority] : '#6b7280';
      const TypeIcon = tk.type ? TYPE_ICON[tk.type] : IconMessage2;
      const isActive = tk.id === selectedId;
      return (
        <button
          key={tk.id}
          type="button"
          className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
          style={{ borderLeftColor: statusColor }}
          onClick={() => tk.id !== undefined && setSelectedId(tk.id)}
        >
          <span className={styles.rowRef}>{ticketRef(tk.id)}</span>
          <span className={styles.rowMain}>
            <span className={styles.rowTitle}>{tk.title}</span>
            <span className={styles.rowTenant}>
              <IconBuildingStore size={12} />
              {tk.tenantName || '—'}
            </span>
          </span>
          <span className={styles.rowType}>
            <TypeIcon size={14} />
            {tk.type ? t(`types.${tk.type}`) : '—'}
          </span>
          {tk.priority && (
            <span
              className={styles.rowPriority}
              style={{ color: priorityColor, backgroundColor: tint(priorityColor) }}
            >
              {t(`priorities.${tk.priority}`)}
            </span>
          )}
          {tk.status && (
            <span className={styles.rowStatus} style={{ color: statusColor }}>
              {t(`statuses.${tk.status}`)}
            </span>
          )}
          <span className={styles.rowTime}>{formatRelative(tk.lastActivityAt || tk.created, locale)}</span>
        </button>
      );
    });
  };

  return (
    <div className={styles.wrap}>
      <p className={styles.intro}>{t('adminConsole.intro')}</p>

      <div className={styles.statGrid}>
        {statTiles.map((tile) => (
          <button
            key={tile.key || 'total'}
            type="button"
            className={`${styles.statTile} ${statusFilter === tile.key && tile.key ? styles.statTileActive : ''}`}
            onClick={() => setStatusFilter((prev) => (prev === tile.key ? '' : tile.key))}
          >
            <div className={styles.statValue}>{tile.value}</div>
            <div className={styles.statLabel}>{tile.label}</div>
          </button>
        ))}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchInput}>
          <IconSearch size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('adminConsole.searchPlaceholder')}
          />
        </div>
        <div className={styles.selects}>
          <select
            className={styles.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label={t('filters.status')}
          >
            <option value="">{t('filters.status')}</option>
            {STATUS_VALUES.map((status) => (
              <option key={status} value={status}>
                {t(`statuses.${status}`)}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label={t('filters.type')}
          >
            <option value="">{t('filters.type')}</option>
            {TYPE_VALUES.map((type) => (
              <option key={type} value={type}>
                {t(`types.${type}`)}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            aria-label={t('filters.priority')}
          >
            <option value="">{t('filters.priority')}</option>
            {PRIORITY_VALUES.map((priority) => (
              <option key={priority} value={priority}>
                {t(`priorities.${priority}`)}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            aria-label={t('filters.sortBy')}
          >
            <option value="recent">{t('sort.recent')}</option>
            <option value="created">{t('sort.created')}</option>
            <option value="priority">{t('sort.priority')}</option>
          </select>
          {hasFilters && (
            <button className={styles.linkButton} onClick={clearFilters} type="button">
              {t('filters.clear')}
            </button>
          )}
        </div>
      </div>

      <div className={styles.count}>{t('list.count', { count: filtered.length })}</div>

      <div className={styles.list}>{renderList()}</div>

      <AdminFeedbackDetail
        ticketId={selectedId}
        fallbackTicket={selected}
        isOpen={selectedId !== null}
        onClose={() => setSelectedId(null)}
        onDeleted={() => setSelectedId(null)}
      />
    </div>
  );
};

export default AdminFeedbackSection;
